import { cn } from '@/lib/utils';
import type { Basis, CalcMethod, Confidence, InferenceSource } from '@everplan/shared';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export interface ChargeFormulaBadgeProps {
  calcMethod: CalcMethod;
  rateBps: number | null;
  basis: Basis | null;
  inferenceSource: InferenceSource;
  confidence: Confidence | null;
  sectionLabels?: Record<string, string>;
  chargeLabels?: Record<string, string>;
  /** Optional package context — when provided, the badge renders a hover
   *  tooltip showing the full computation (rate × basis = amount). */
  packageContext?: {
    lineItemTotal: number;
    addOnTotal: number;
    sectionTotals: Record<string, number>;
    siblingResolvedAmounts: Record<string, number>;
  };
  onClick?: () => void;
}

function computeBasisTotal(
  basis: Basis,
  ctx: NonNullable<ChargeFormulaBadgeProps['packageContext']>,
): number {
  switch (basis.kind) {
    case 'package_subtotal':
      return ctx.lineItemTotal + ctx.addOnTotal;
    case 'sections':
      return basis.sections.reduce((sum, s) => sum + (ctx.sectionTotals[s] ?? 0), 0);
    case 'charges':
      return basis.chargeIds.reduce((sum, id) => sum + (ctx.siblingResolvedAmounts[id] ?? 0), 0);
    case 'composite':
      return basis.parts.reduce((sum, p) => sum + computeBasisTotal(p, ctx), 0);
  }
}

function formatDollars(cents: number): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
}

function bankersRound(value: number): number {
  const sign = value < 0 ? -1 : 1;
  const abs = Math.abs(value);
  const truncated = Math.trunc(abs);
  const frac = abs - truncated;
  let rounded: number;
  if (Math.abs(frac - 0.5) < 1e-9) {
    rounded = truncated % 2 === 0 ? truncated : truncated + 1;
  } else {
    rounded = Math.round(abs);
  }
  return sign * (rounded === 0 ? 0 : rounded);
}

function formatRate(rateBps: number): string {
  const pct = (rateBps / 100).toFixed(2).replace(/\.00$/, '');
  return `${pct}%`;
}

function formatBasis(
  basis: Basis,
  sectionLabels?: Record<string, string>,
  chargeLabels?: Record<string, string>,
): string {
  switch (basis.kind) {
    case 'package_subtotal':
      return 'subtotal';
    case 'sections':
      return basis.sections
        .map((s) => (sectionLabels?.[s] ?? (s.charAt(0).toUpperCase() + s.slice(1))))
        .join(' + ');
    case 'charges':
      return basis.chargeIds
        .map((id) => chargeLabels?.[id] ?? 'a charge')
        .join(' + ');
    case 'composite':
      return basis.parts
        .map((p) => formatBasis(p, sectionLabels, chargeLabels))
        .join(' + ');
  }
}

export function ChargeFormulaBadge({
  calcMethod,
  rateBps,
  basis,
  inferenceSource,
  confidence,
  sectionLabels,
  chargeLabels,
  packageContext,
  onClick,
}: ChargeFormulaBadgeProps) {
  if (calcMethod === 'flat') return null;

  const isConfirmed = inferenceSource === 'user-overridden' || confidence === 'high';
  const isMedium = !isConfirmed && confidence === 'medium';
  const isReview = !isConfirmed && !isMedium && confidence === 'low';

  const label =
    rateBps != null && basis != null
      ? `${formatRate(rateBps)} · ${formatBasis(basis, sectionLabels, chargeLabels)}`
      : rateBps != null
        ? formatRate(rateBps)
        : null;

  // Build the tooltip content showing the full computation:
  //   "9% × $9,405.00 (Food & Beverage) = $846.45"
  // Only renders when we have rate + basis + packageContext to compute against.
  const tooltipNode =
    rateBps != null && basis != null && packageContext != null
      ? (() => {
          const basisTotal = computeBasisTotal(basis, packageContext);
          const computed = bankersRound((rateBps * basisTotal) / 10000);
          const basisLabel = formatBasis(basis, sectionLabels, chargeLabels);
          return (
            <div className="space-y-0.5 text-left">
              <div className="font-medium">{formatRate(rateBps)} of {basisLabel}</div>
              <div className="tabular-nums opacity-80">
                {formatRate(rateBps)} × {formatDollars(basisTotal)} = {formatDollars(computed)}
              </div>
            </div>
          );
        })()
      : null;

  const wrapInTooltip = (node: React.ReactNode) => {
    if (!tooltipNode) return node;
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            {node}
          </TooltipTrigger>
          <TooltipContent side="top" align="end">
            {tooltipNode}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  if (isReview) {
    return wrapInTooltip(
      <span
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
        className="inline-flex items-center rounded-full bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-400 cursor-pointer"
      >
        Review basis
      </span>,
    );
  }

  if (!label) return null;

  if (isMedium) {
    return wrapInTooltip(
      <span
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
        className={cn(
          'inline-flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400 cursor-pointer',
          onClick && 'hover:underline',
        )}
      >
        <span aria-hidden>●</span>
        {label}
      </span>,
    );
  }

  // Confirmed (user or high)
  return wrapInTooltip(
    <span
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
      className={cn(
        'inline-flex items-center text-xs text-muted-foreground',
        onClick && 'cursor-pointer hover:underline',
      )}
    >
      {label}
    </span>,
  );
}
